import { Component, Input, OnInit, Signal } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';;
import * as _ from "underscore";
import { BaseComponent } from '../models/components/BaseComponent';
import { ComponentService } from '../services/component.service';
import { MatDialog } from '@angular/material/dialog';
import { AdvancedPropertyWindowComponent } from '../advanced-property-window/advanced-property-window.component';
import { COMPONENT_MAP } from '../advanced-property-window/component-map.const';
import { BaseDimensionComponent } from '../models/components/commonComponents/BaseDimensionComponent';
import { NamingComponent } from '../models/components/commonComponents/NamingComponent';
import { CloneService } from '../services/clone.service';
import { ModalData } from '../advanced-property-window/modal-data.model';
import { componentArrayHasChanges } from '../generics-helper/component-array-has-changes.function';
import { getComponentFromSource } from '../generics-helper/getComponentFromSource.generic';
import { Box } from '../models/other/box.model';
import { ComponentNames } from '../helper/componentNames';
import { IdentificationComponent } from '../models/components/commonComponents/IdentificationComponent';
import { ComponentChanged } from '../helper/componentChanged.model';
import { IValidationService } from '../models/interfaces/iValidationService.interface';
import { ValidationResult } from '../models/validationresult.model';
import { ModalWithMessageLoop } from '../Modal/modalWithMessageLoop';
import { AxisEnum } from '../models/enums/AxisEnum';
import { konvaPositionToTopLeftObjectCornerPosition, topLeftObjectCornerPositionToKonvaPosition } from '../helper/positionMapper';
import { compareObjectsForEquality } from '../helper/compareObjectsForEquality';
import { SeatWithStorageComponent } from '../models/components/objectComponents/SeatWithStorageComponent';
import { DockingBehaviourComponent } from '../models/components/commonComponents/DockingBehaviourComponent';
import { AuthService } from '../services/auth.service';
import { userHasFeature } from '../helper/userHasFeature';
import { FeatureEnum } from '../models/enums/FeatureEnum';
import { InputHelper } from 'src/app/helper/inputHelper';

@Component({
  selector: 'app-property-window',
  templateUrl: './property-window.component.html',
  styleUrls: ['./property-window.component.scss'],
  providers: [CloneService]
})
export class PropertyWindowComponent implements OnInit {
  // the component which will be loaded into the MatDialog
  viewComponent: any;
  // array of components for compare operations
  originalComponents: Array<BaseComponent>;
  editableComponents: Array<BaseComponent>;
  // propertys for the view
  box: Box;
  name: string;
  uid: string;
  posX: number;
  posY: number;
  rotation: number;
  marginInputDisabled: boolean;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  label1: string = 'Length:';
  label2: string = 'Width:';
  label3: string = 'Toolbox Name:';
  label4: string = 'Identifier Name:';
  label5: string = 'Position X: ';
  label6: string = 'Position Y: ';
  label7: string = 'Margin Top: ';
  label8: string = 'Margin Bottom: ';
  label9: string = 'Margin Left: ';
  label10: string = 'Margin Right: ';
  action1: string = 'Advanced Properties';
  // property which maps the component key to the corosponding view component
  componentMap: Map<any, any> = COMPONENT_MAP;
  // used for change detection in the components array
  helperArray: ComponentChanged[];
  // used for managing subscriptions
  private destroyed$: Subject<void> = new Subject();
  private validationService: IValidationService;
  lengthDisabled: boolean;
  enableAdvancedProperties: boolean;
  showToolboxName: boolean;
  integerOnly = InputHelper.integerOnly;
  integerOnlyWithNegatives = InputHelper.integerOnlyWithNegatives;
  @Input('additionalData') addtionalData: Signal<any>;

  constructor(private componentService: ComponentService, private dialog: MatDialog, private cloneService: CloneService, private authService: AuthService) {
  }

  ngOnInit() {
    this.componentService.getCurrentComponentsObservable().pipe(takeUntil(this.destroyed$)).subscribe((value: BaseComponent[]) => {
      this.setPropertyDefaultValues();
      if (value) {
        this.originalComponents = this.cloneService.deepClone(value);
        this.editableComponents = this.cloneService.deepClone(value);
        this.initProperties();
        this.setViewComponent();
        this.checkForShowAdvancedProperties();
      }
    });
    this.validationService = this.componentService.getValidationService();
  }


  checkForShowAdvancedProperties() {
    let tmpShowPartitionWallAPW = userHasFeature(this.authService.getCurrentFeatures(), FeatureEnum.CanShowAdvancedPropertiesPartitionWall);
    let tmpShowUnusableSpaceAPW = userHasFeature(this.authService.getCurrentFeatures(), FeatureEnum.CanShowAdvancedPropertiesUnusableSpace);

    let partitionWallComponent = this.originalComponents.filter(f => f.componentType == ComponentNames.partitionWallComponent)[0];
    if (partitionWallComponent != null && partitionWallComponent != undefined) {
      if (!tmpShowPartitionWallAPW) {
        this.enableAdvancedProperties = false;
        return;
      }
    }

    let nonUsableSpaceComponent = this.originalComponents.filter(f => f.componentType == ComponentNames.nonUsableSpaceComponent)[0];
    if (nonUsableSpaceComponent != null && nonUsableSpaceComponent != undefined) {
      if (!tmpShowUnusableSpaceAPW) {
        this.enableAdvancedProperties = false;
        return;
      }
    }

    this.enableAdvancedProperties = true;
  }


  initProperties() {
    let seatWithStorage = this.cloneService.deepClone(getComponentFromSource<SeatWithStorageComponent>(ComponentNames.seatWithStorageComponent, this.editableComponents));
    if (seatWithStorage) {
      this.lengthDisabled = true;
    }

    let baseDimensionsComponent = this.cloneService.deepClone(getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents));
    if (baseDimensionsComponent && baseDimensionsComponent.box) {
      this.initBaseDimension(baseDimensionsComponent);
    }

    let namingComponent = this.cloneService.deepClone(getComponentFromSource<NamingComponent>(ComponentNames.namingComponent, this.editableComponents));
    if (namingComponent) {
      this.initNamingComponent(namingComponent);
    }

    let identificationComponent = this.cloneService.deepClone(getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, this.editableComponents));
    if (identificationComponent) {
      this.initIdentificationComponent(identificationComponent);
    }

    let dockingBehaviourComponent = this.cloneService.deepClone(getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents));
    if (dockingBehaviourComponent) {
      this.marginInputDisabled = false;
      this.initDockingBehaviourComponent(dockingBehaviourComponent);
    }
  }

  setPropertyDefaultValues() {
    this.viewComponent = null;
    this.editableComponents = new Array<BaseComponent>();
    this.originalComponents = new Array<BaseComponent>();
    this.helperArray = new Array<ComponentChanged>();
    this.box = null;
    this.posX = null;
    this.posY = null;
    this.rotation = null;
    this.name = null;
    this.uid = null;
    this.lengthDisabled = false;
    this.marginInputDisabled = true;
    // ToDo: show toolbox name needs handling for true case when custom object defining is introduced!
    this.showToolboxName = false;
  }

  initIdentificationComponent(identificationComponent: IdentificationComponent) {
    this.uid = identificationComponent.userId;
    let cc: ComponentChanged = new ComponentChanged(
      identificationComponent.componentType,
      () => { return this.uid; },
      () => { return getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, this.editableComponents).userId; },
      (value) => { getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, this.editableComponents).userId = value; },
      () => { return compareObjectsForEquality(cc.getCurrentValue(), cc.getOriginalValue()); }
    );
    this.pushToHelperArray(cc);
  }

  initDockingBehaviourComponent(dockingBehaviourComponent: DockingBehaviourComponent) {
    this.marginTop = dockingBehaviourComponent.offsetBox.backOffset;
    let cc: ComponentChanged = new ComponentChanged(
      dockingBehaviourComponent.componentType,
      () => { return this.marginTop; },
      () => { return getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.backOffset; },
      (value) => { getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.backOffset = value; },
      () => { return compareObjectsForEquality(cc.getCurrentValue(), cc.getOriginalValue()); }
    );
    this.pushToHelperArray(cc);

    this.marginBottom = dockingBehaviourComponent.offsetBox.frontOffset;
    let cc1: ComponentChanged = new ComponentChanged(
      dockingBehaviourComponent.componentType,
      () => { return this.marginBottom; },
      () => { return getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.frontOffset; },
      (value) => { getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.frontOffset = value; },
      () => { return compareObjectsForEquality(cc1.getCurrentValue(), cc1.getOriginalValue()); }
    );
    this.pushToHelperArray(cc1);

    this.marginLeft = dockingBehaviourComponent.offsetBox.leftOffset;
    let cc2: ComponentChanged = new ComponentChanged(
      dockingBehaviourComponent.componentType,
      () => { return this.marginLeft; },
      () => { return getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.leftOffset; },
      (value) => { getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.leftOffset = value; },
      () => { return compareObjectsForEquality(cc2.getCurrentValue(), cc2.getOriginalValue()); }
    );
    this.pushToHelperArray(cc2);

    this.marginRight = dockingBehaviourComponent.offsetBox.rightOffset;
    let cc3: ComponentChanged = new ComponentChanged(
      dockingBehaviourComponent.componentType,
      () => { return this.marginRight; },
      () => { return getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.rightOffset; },
      (value) => { getComponentFromSource<DockingBehaviourComponent>(ComponentNames.dockingBehaviorComponent, this.editableComponents).offsetBox.rightOffset = value; },
      () => { return compareObjectsForEquality(cc3.getCurrentValue(), cc3.getOriginalValue()); }
    );
    this.pushToHelperArray(cc3);
  }

  initNamingComponent(namingComponent: NamingComponent) {
    this.name = namingComponent.name;
    let cc: ComponentChanged = new ComponentChanged(
      namingComponent.componentType,
      () => { return this.name; },
      () => { return getComponentFromSource<NamingComponent>(ComponentNames.namingComponent, this.editableComponents).name; },
      (value) => { getComponentFromSource<NamingComponent>(ComponentNames.namingComponent, this.editableComponents).name = value; },
      () => { return compareObjectsForEquality(cc.getCurrentValue(), cc.getOriginalValue()); }
    );
    this.pushToHelperArray(cc);
  }

  initBaseDimension(baseDimensionsComponent: BaseDimensionComponent) {
    const axisX = AxisEnum.X;
    const axisY = AxisEnum.Y;
    this.box = baseDimensionsComponent.box;
    let cc: ComponentChanged = new ComponentChanged(
      baseDimensionsComponent.componentType,
      () => { return this.box; },
      () => { return getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents).box; },
      (value) => { getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents).box = value; },
      () => { return compareObjectsForEquality(cc.getCurrentValue(), cc.getOriginalValue()); }
    );
    this.pushToHelperArray(cc);

    this.rotation = baseDimensionsComponent.rotation;

    this.posX = konvaPositionToTopLeftObjectCornerPosition(baseDimensionsComponent.posX, axisX, this.box, this.rotation);
    let cc1: ComponentChanged = new ComponentChanged(
      baseDimensionsComponent.componentType,
      () => { return this.posX; },
      () => { return getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents).posX; },
      (value) => { getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents).posX = topLeftObjectCornerPositionToKonvaPosition(value, axisX, this.box, this.rotation); },
      () => { return compareObjectsForEquality(cc1.getCurrentValue(), konvaPositionToTopLeftObjectCornerPosition(cc1.getOriginalValue(), axisX, this.box, this.rotation)); }
    );
    this.pushToHelperArray(cc1);

    this.posY = konvaPositionToTopLeftObjectCornerPosition(baseDimensionsComponent.posY, axisY, this.box, this.rotation);
    let cc2: ComponentChanged = new ComponentChanged(
      baseDimensionsComponent.componentType,
      () => { return this.posY; },
      () => { return getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents).posY; },
      (value) => { getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, this.editableComponents).posY = topLeftObjectCornerPositionToKonvaPosition(value, null, this.box, this.rotation); },
      () => { return compareObjectsForEquality(cc2.getCurrentValue(), konvaPositionToTopLeftObjectCornerPosition(cc2.getOriginalValue(), axisY, this.box, this.rotation)); }
    );
    this.pushToHelperArray(cc2);
  }

  pushToHelperArray(cc: ComponentChanged) {
    this.helperArray.push(cc);
  }

  // opens the advanced property window modal-view
  openAdvancedProperties() {
    let modalData: ModalData = { components: this.cloneService.deepClone(this.editableComponents), viewComponent: this.viewComponent, additionalData: this.addtionalData() };

    let dialogWidth = '100vw';
    let dialogRef = this.dialog.open(AdvancedPropertyWindowComponent, {
      width: dialogWidth,
      data: modalData
    });

    dialogRef.afterClosed().subscribe((modifiedComponents: Array<BaseComponent>) => {
      if (modifiedComponents) {
        if (!componentArrayHasChanges(this.originalComponents, modifiedComponents)) {
          return;
        } else {
          this.emitComponentChanges(modifiedComponents);
        }
      } else {
        // do nothing in this case
      }
    });
  }

  // sets the corrosponding view component for the interior object
  setViewComponent(): any {
    this.viewComponent = null;
    this.editableComponents.forEach((component: any) => {
      // early exit outer loop
      if (component.componentType === ComponentNames.viewComponent) {
        for (let key of this.componentMap.keys()) {
          if (component.viewName !== undefined && component.viewName === key) {
            let comp = this.componentMap.get(key);
            if (comp !== undefined && comp !== null) {
              this.viewComponent = comp;
              return;
            }
          }
        }
      }
    });
  }

  // check if a edit command has to be created or not
  checkForComponentChanges() {
    if (!this.checkComponentsForEquality()) {

      let validationresult: ValidationResult = this.validationService.isValid(this.helperArray);
      if (validationresult.isValid) {

        this.helperArray.forEach(element => {
          element.setOriginalValue(element.getCurrentValue());
        });

        this.emitComponentChanges(this.editableComponents);
      }
      else {
        this.showValidationResult(validationresult);
      }
    }
  }
  showValidationResult(validationresult: ValidationResult) {
    let data = validationresult.data;

    let dialogRef = this.dialog.open(ModalWithMessageLoop, {
      data: {
        data: data,
        title: 'Validation error',
        saveButtonText: 'Okay',
        showCancel: false
      }
    });
  }

  emitComponentChanges(components: BaseComponent[]) {
    this.componentService.setCurrentComponents(components);
    this.componentService.componentsChanged.emit();
  }

  // returns false if any component is not equal to its original component
  checkComponentsForEquality(): boolean {
    let isEqual = true;
    for (let index = 0; index < this.helperArray.length; index++) {
      const element = this.helperArray[index];
      if (!element.isEqual()) {
        isEqual = false;
        break;
      }
    }
    return isEqual;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}